import localforage from 'localforage'

export interface QueuedMutation {
 id: string
 local_id?: string
 idempotency_key?: string
 type?: string
 url: string
 method: 'POST' | 'PUT' | 'DELETE' | 'PATCH'
 data?: any
 payload?: any
 headers?: Record<string, string>
 tenant_id?: string | null
 branch_id?: string | null
 user_id?: string | number | null
 created_at_local?: string
 status?: 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict'
 last_error?: string | null
 timestamp: number
 retryCount: number
 sliceName: string
 actionType: string
}

const queueStore = localforage.createInstance({
 name: 'erp-offline',
 storeName: 'mutation-queue',
})

const QUEUE_KEY = 'pending_mutations'
const UNKNOWN_SCOPE = 'unknown'

function readAuthScope(): Pick<QueuedMutation, 'tenant_id' | 'branch_id' | 'user_id'> {
 if (typeof localStorage === 'undefined') {
 return { tenant_id: UNKNOWN_SCOPE, branch_id: UNKNOWN_SCOPE, user_id: UNKNOWN_SCOPE }
 }

 let parsed: any = null
 const raw = localStorage.getItem('authUser')
 if (raw) {
 try {
 parsed = JSON.parse(raw)
 } catch {
 parsed = null
 }
 }
 const user = parsed?.user ?? parsed
 return {
 tenant_id:
 parsed?.tenant_slug ??
 localStorage.getItem('tenant_slug') ??
 user?.tenant_id ??
 user?.company_id ??
 UNKNOWN_SCOPE,
 branch_id:
 localStorage.getItem('selected_branch_id') ??
 user?.current_branch_id ??
 UNKNOWN_SCOPE,
 user_id: user?.id ?? UNKNOWN_SCOPE,
 }
}

export async function getQueue(): Promise<QueuedMutation[]> {
 const items = await queueStore.getItem<QueuedMutation[]>(QUEUE_KEY)
 return items ?? []
}

export async function enqueue(
 mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>
): Promise<QueuedMutation> {
 const queue = await getQueue()
 const scope = readAuthScope()
 const localId = crypto.randomUUID()
 const item: QueuedMutation = {
 ...mutation,
 id: localId,
 local_id: mutation.local_id ?? localId,
 idempotency_key: mutation.idempotency_key ?? localId,
 type: mutation.type ?? mutation.actionType,
 payload: mutation.payload ?? mutation.data,
 tenant_id: mutation.tenant_id ?? scope.tenant_id,
 branch_id: mutation.branch_id ?? scope.branch_id,
 user_id: mutation.user_id ?? scope.user_id,
 created_at_local: mutation.created_at_local ?? new Date().toISOString(),
 status: mutation.status ?? 'pending',
 last_error: mutation.last_error ?? null,
 timestamp: Date.now(),
 retryCount: 0,
 }
 queue.push(item)
 await queueStore.setItem(QUEUE_KEY, queue)
 return item
}

export async function dequeue(id: string): Promise<void> {
 const queue = await getQueue()
 await queueStore.setItem(QUEUE_KEY, queue.filter((m) => m.id !== id))
}

export async function dequeueMany(ids: Set<string>): Promise<void> {
 if (ids.size === 0) return
 const queue = await getQueue()
 await queueStore.setItem(QUEUE_KEY, queue.filter((m) => !ids.has(m.id)))
}

export async function incrementRetry(id: string, lastError?: string): Promise<number> {
 const queue = await getQueue()
 const idx = queue.findIndex((m) => m.id === id)
 if (idx < 0) return 0
 queue[idx].retryCount++
 queue[idx].status = queue[idx].retryCount >= 5 ? 'failed' : 'pending'
 queue[idx].last_error = lastError ?? queue[idx].last_error ?? null
 await queueStore.setItem(QUEUE_KEY, queue)
 return queue[idx].retryCount
}

export async function clearQueue(): Promise<void> {
  await queueStore.setItem(QUEUE_KEY, [])
}

/** Reset failed mutation rows to pending for another sync pass. */
export async function requeueFailedMutations(): Promise<number> {
  const queue = await getQueue()
  let n = 0
  const next = queue.map((m) => {
    if (m.status !== 'failed') return m
    n += 1
    return { ...m, status: 'pending' as const, retryCount: 0, last_error: null }
  })
  if (n > 0) await queueStore.setItem(QUEUE_KEY, next)
  return n
}

/** Reset conflict mutation rows to pending for another sync pass. */
export async function requeueConflictMutations(): Promise<number> {
  const queue = await getQueue()
  let n = 0
  const next = queue.map((m) => {
    if (m.status !== 'conflict') return m
    n += 1
    return { ...m, status: 'pending' as const, retryCount: 0, last_error: null }
  })
  if (n > 0) await queueStore.setItem(QUEUE_KEY, next)
  return n
}

/** Reset a single failed/conflict mutation to pending for another sync pass. */
export async function requeueMutationById(id: string): Promise<boolean> {
  const queue = await getQueue()
  const idx = queue.findIndex((m) => m.id === id)
  if (idx < 0) return false
  const status = queue[idx].status
  if (status !== 'failed' && status !== 'conflict') return false
  queue[idx] = { ...queue[idx], status: 'pending', retryCount: 0, last_error: null }
  await queueStore.setItem(QUEUE_KEY, queue)
  return true
}

/** Failed + conflict rows shown in the “other ops” review dialog. */
export async function getFailedAndConflictMutations(): Promise<QueuedMutation[]> {
  const queue = await getQueue()
  return queue.filter((m) => m.status === 'failed' || m.status === 'conflict')
}

export async function getQueueCount(): Promise<number> {
 const queue = await getQueue()
 return queue.length
}
